import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SecretInput, weakPassword } from "@/components/login/Secret";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

/** Blocking dialog when the administrator required a password change at next sign-in. */
export const ForcePasswordChange = () => {
  const { refreshUser } = useAuth();
  const [v, setV] = useState({ current: "", next: "", again: "" });
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const err = (v.next && weakPassword(v.next)) || (v.again && v.next !== v.again && "The passwords don't match") || (v.next && v.next === v.current && "Choose a password different from the current one");
  const save = async () => {
    setBusy(true);
    try { await api.post("/auth/password", { current_password: v.current, new_password: v.next }); toast.success("Password changed"); await refreshUser(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open>
      <DialogContent className="max-w-md [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="force-password-dialog">
        <DialogTitle>Choose a new password</DialogTitle>
        <DialogDescription>The administrator set a temporary password for you. Choose your own to continue.</DialogDescription>
        <div className="space-y-2">
          <SecretInput value={v.current} onChange={(x) => setV({ ...v, current: x })} shown={shown} onToggle={() => setShown(!shown)} placeholder="Current (temporary) password" autoComplete="current-password" testid="force-password-current" />
          <SecretInput value={v.next} onChange={(x) => setV({ ...v, next: x })} shown={shown} onToggle={() => setShown(!shown)} placeholder="New password" autoComplete="new-password" testid="force-password-new" />
          <SecretInput value={v.again} onChange={(x) => setV({ ...v, again: x })} shown={shown} onToggle={() => setShown(!shown)} placeholder="Repeat new password" autoComplete="new-password" testid="force-password-again" />
          {err && <p className="text-xs text-rose-700" data-testid="force-password-error">{err}</p>}
        </div>
        <Button className="h-11 bg-teal-600 hover:bg-teal-700" disabled={busy || !v.current || !v.next || v.next !== v.again || !!err} onClick={save} data-testid="force-password-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save new password</Button>
      </DialogContent>
    </Dialog>
  );
};
